/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { getCompressedDocumentDocxBlob } from '@ecsper/host-word';
import type { UUIDv7 } from '@ecsper/types';
import type { VaultClient } from '@ecsper/vault-client';

import type { WopiContext } from './wopiMode.js';

/**
 * Autosave service for governed Vault saves.
 *
 * Behaviour:
 * - Checks for changes every 30 seconds
 * - Compares a content hash to skip no-op saves
 * - Debounces: waits 5 seconds after the last document change before saving
 *   (so it does not save mid-keystroke)
 * - Non-WOPI: posts to /save-docx/ to create a minor version
 * - WOPI Mode B: posts DOCX bytes to PutFile on the WOPI host
 * - Exposes start(), stop(), saveNow(), saveMajorVersion(), and notifyDocumentChanged()
 */

const AUTOSAVE_INTERVAL_MS = 30_000;
const DEBOUNCE_AFTER_CHANGE_MS = 5_000;
/** Office refreshes WOPI locks every 15 minutes (ECS-DD-WOPI-HOST-001 §A8). */
const WOPI_LOCK_REFRESH_MS = 15 * 60 * 1_000;

export interface AutosaveServiceOptions {
  reportId: UUIDv7;
  vaultClient: VaultClient;
  /** When set, saves target PutFile on the WOPI host instead of Workspace. */
  wopiContext?: WopiContext;
  /** Defaults to {@link getCompressedDocumentDocxBlob}. */
  getDocxBlob?: () => Promise<Blob>;
  onSaveStart?: () => void;
  onSaveComplete: (version: number) => void;
  onSaveError: (error: Error) => void;
  /** Fired when document content differs from the last saved hash. */
  onUnsavedChange?: () => void;
  /** Override interval for unit tests. */
  intervalMs?: number;
  /** Override debounce window for unit tests. */
  debounceMs?: number;
  /** Override WOPI lock refresh interval for unit tests. */
  wopiLockRefreshMs?: number;
  /** Injectable timer hooks for unit tests. */
  timerHooks?: {
    setInterval: (handler: () => void, ms: number) => ReturnType<typeof setInterval>;
    clearInterval: (id: ReturnType<typeof setInterval>) => void;
  };
  /** Injectable fetch for unit tests. */
  fetchFn?: typeof fetch;
}

interface SaveResult {
  versionNumber: number;
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash.toString(36);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function parseWopiVersionNumber(headerValue: string | null): number {
  if (headerValue === null || headerValue === '') {
    return 0;
  }
  const parsed = Number.parseInt(headerValue, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export class AutosaveService {
  private readonly reportId: UUIDv7;

  private readonly vaultClient: VaultClient;

  private readonly wopiContext: WopiContext | undefined;

  private readonly getDocxBlob: () => Promise<Blob>;

  private readonly onSaveStart: () => void;

  private readonly onSaveComplete: (version: number) => void;

  private readonly onSaveError: (error: Error) => void;

  private readonly onUnsavedChange: (() => void) | undefined;

  private readonly intervalMs: number;

  private readonly debounceMs: number;

  private readonly wopiLockRefreshMs: number;

  private readonly timerHooks: NonNullable<AutosaveServiceOptions['timerHooks']>;

  private readonly fetchFn: typeof fetch;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  private lockRefreshId: ReturnType<typeof setInterval> | null = null;

  private currentLockId: string | null = null;

  private lastContentHash: string | null = null;

  private lastChangeTime = 0;

  private saving = false;

  public constructor(options: AutosaveServiceOptions) {
    this.reportId = options.reportId;
    this.vaultClient = options.vaultClient;
    this.wopiContext = options.wopiContext;
    this.getDocxBlob = options.getDocxBlob ?? (() => getCompressedDocumentDocxBlob());
    this.onSaveStart = options.onSaveStart ?? (() => undefined);
    this.onSaveComplete = options.onSaveComplete;
    this.onSaveError = options.onSaveError;
    this.onUnsavedChange = options.onUnsavedChange;
    this.intervalMs = options.intervalMs ?? AUTOSAVE_INTERVAL_MS;
    this.debounceMs = options.debounceMs ?? DEBOUNCE_AFTER_CHANGE_MS;
    this.wopiLockRefreshMs = options.wopiLockRefreshMs ?? WOPI_LOCK_REFRESH_MS;
    this.timerHooks = options.timerHooks ?? {
      setInterval: (handler, ms) => setInterval(handler, ms),
      clearInterval: (id) => clearInterval(id),
    };
    this.fetchFn = options.fetchFn ?? fetch.bind(globalThis);
  }

  /** Start the autosave interval (acquires a WOPI lock first when in WOPI mode). */
  public start(): void {
    if (this.intervalId !== null) {
      return;
    }

    if (this.wopiContext?.isWopiMode === true) {
      void this.acquireWopiLock()
        .then(() => {
          this.beginAutosaveInterval();
          this.beginLockRefresh();
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          this.onSaveError(error);
        });
      return;
    }

    this.beginAutosaveInterval();
  }

  /** Stop the autosave interval and release any WOPI lock. */
  public stop(): void {
    if (this.intervalId !== null) {
      this.timerHooks.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.lockRefreshId !== null) {
      this.timerHooks.clearInterval(this.lockRefreshId);
      this.lockRefreshId = null;
    }
    if (this.wopiContext?.isWopiMode === true && this.currentLockId !== null) {
      void this.releaseWopiLock();
    }
  }

  /** Signal that the document changed (restarts debounce, may mark unsaved). */
  public notifyDocumentChanged(): void {
    this.lastChangeTime = Date.now();
    void this.detectUnsaved();
  }

  /** Manual save; bypasses debounce and interval timing. */
  public async saveNow(): Promise<void> {
    await this.performSave({ saveType: 'manual', force: true });
  }

  /** Create a major milestone version with an optional note. */
  public async saveMajorVersion(versionNote = ''): Promise<void> {
    await this.performSave({
      saveType: 'final',
      force: true,
      isMajor: true,
      versionNote,
    });
  }

  private beginAutosaveInterval(): void {
    if (this.intervalId !== null) {
      return;
    }
    this.intervalId = this.timerHooks.setInterval(() => {
      void this.checkAndSave();
    }, this.intervalMs);
  }

  private beginLockRefresh(): void {
    if (this.lockRefreshId !== null || this.wopiContext?.isWopiMode !== true) {
      return;
    }
    this.lockRefreshId = this.timerHooks.setInterval(() => {
      void this.refreshWopiLock().catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        this.onSaveError(error);
      });
    }, this.wopiLockRefreshMs);
  }

  private async detectUnsaved(): Promise<void> {
    if (this.onUnsavedChange === undefined) {
      return;
    }
    try {
      const blob = await this.getDocxBlob();
      const base64 = await blobToBase64(blob);
      const hash = simpleHash(base64);
      if (this.lastContentHash !== null && hash !== this.lastContentHash) {
        this.onUnsavedChange();
      }
    } catch {
      /* Ignore hash probe errors; the next save attempt will surface them. */
    }
  }

  private async checkAndSave(): Promise<void> {
    await this.performSave({ saveType: 'auto', force: false });
  }

  private async performSave(params: {
    saveType: 'auto' | 'manual' | 'final';
    force: boolean;
    isMajor?: boolean;
    versionNote?: string;
  }): Promise<void> {
    if (this.saving) {
      return;
    }

    if (!params.force && Date.now() - this.lastChangeTime < this.debounceMs) {
      return;
    }

    try {
      const blob = await this.getDocxBlob();
      const base64 = await blobToBase64(blob);
      const hash = simpleHash(base64);

      if (
        params.isMajor !== true &&
        this.lastContentHash !== null &&
        hash === this.lastContentHash
      ) {
        return;
      }

      this.saving = true;
      this.onSaveStart();

      const result =
        this.wopiContext?.isWopiMode === true
          ? await this.saveToWopi(blob)
          : await this.saveToWorkspace(blob, params);

      this.lastContentHash = hash;
      this.lastChangeTime = 0;
      this.onSaveComplete(result.versionNumber);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.onSaveError(error);
    } finally {
      this.saving = false;
    }
  }

  private async saveToWorkspace(
    blob: Blob,
    params: {
      saveType: 'auto' | 'manual' | 'final';
      isMajor?: boolean;
      versionNote?: string;
    },
  ): Promise<SaveResult> {
    const result = await this.vaultClient.saveDocx({
      reportId: this.reportId,
      docxBlob: blob,
      saveType: params.saveType,
      ...(params.isMajor === true ? { isMajor: true } : {}),
      ...(params.versionNote !== undefined && params.versionNote !== ''
        ? { versionNote: params.versionNote }
        : {}),
    });

    return { versionNumber: result.versionNumber };
  }

  private async saveToWopi(docxBlob: Blob): Promise<SaveResult> {
    const ctx = this.wopiContext;
    if (ctx?.isWopiMode !== true) {
      throw new Error('WOPI context unavailable');
    }

    if (this.currentLockId === null) {
      await this.acquireWopiLock();
    }

    const response = await this.fetchFn(`${ctx.wopiBaseUrl}/wopi/files/${ctx.fileId}/contents`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${ctx.accessToken}`,
        'X-WOPI-Lock': this.currentLockId ?? '',
      },
      body: docxBlob,
    });

    if (!response.ok) {
      throw new Error(`PutFile failed: ${response.status}`);
    }

    const versionNumber = parseWopiVersionNumber(response.headers.get('X-WOPI-ItemVersion'));
    return { versionNumber };
  }

  private async acquireWopiLock(): Promise<string> {
    const ctx = this.wopiContext;
    if (ctx?.isWopiMode !== true) {
      throw new Error('WOPI context unavailable');
    }

    const lockId = crypto.randomUUID();
    const response = await this.fetchFn(`${ctx.wopiBaseUrl}/wopi/files/${ctx.fileId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${ctx.accessToken}`,
        'X-WOPI-Override': 'LOCK',
        'X-WOPI-Lock': lockId,
      },
    });

    if (response.ok) {
      this.currentLockId = lockId;
      return lockId;
    }

    if (response.status === 409) {
      const currentLock = response.headers.get('X-WOPI-Lock');
      throw new Error(`Document locked by another session: ${currentLock ?? 'unknown'}`);
    }

    throw new Error(`Lock failed: ${response.status}`);
  }

  private async refreshWopiLock(): Promise<void> {
    const ctx = this.wopiContext;
    if (ctx?.isWopiMode !== true || this.currentLockId === null) {
      return;
    }

    const response = await this.fetchFn(`${ctx.wopiBaseUrl}/wopi/files/${ctx.fileId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${ctx.accessToken}`,
        'X-WOPI-Override': 'REFRESH_LOCK',
        'X-WOPI-Lock': this.currentLockId,
      },
    });

    if (!response.ok) {
      throw new Error(`Lock refresh failed: ${response.status}`);
    }
  }

  private async releaseWopiLock(): Promise<void> {
    const ctx = this.wopiContext;
    const lockId = this.currentLockId;
    if (ctx?.isWopiMode !== true || lockId === null) {
      return;
    }

    this.currentLockId = null;

    try {
      await this.fetchFn(`${ctx.wopiBaseUrl}/wopi/files/${ctx.fileId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${ctx.accessToken}`,
          'X-WOPI-Override': 'UNLOCK',
          'X-WOPI-Lock': lockId,
        },
      });
    } catch {
      /* Best-effort unlock on teardown. */
    }
  }
}
